import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 60000 });

export const fetchMarketSnapshot = async () => {
  const { data } = await api.get("/market/snapshot");
  return data;
};

export const runProjection = async (payload) => {
  const { data } = await api.post("/projections/calculate", payload);
  return data;
};

export const critiquePlan = async (plan, summary) => {
  const { data } = await api.post("/critique", { plan, summary });
  return data;
};

export const createCheckout = async (packageId) => {
  const { data } = await api.post("/payments/checkout", {
    package_id: packageId,
    origin_url: window.location.origin,
  });
  return data;
};

export const getCheckoutStatus = async (sessionId) => {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
};

export const verifyUnlockToken = async (token) => {
  const { data } = await api.get(`/payments/verify_token`, { params: { token } });
  return data;
};

export const fetchAffiliates = async () => {
  const { data } = await api.get("/affiliates");
  return data;
};

export const upsertAdvisorListing = async (payload) => {
  const { data } = await api.post("/advisor/listing", payload);
  return data;
};

export const fetchMyListing = async (token) => {
  const { data } = await api.get("/advisor/listing/mine", { params: { token } });
  return data;
};

// ---- Admin ----
export const adminHeaders = (pass) => ({ headers: { "X-Admin-Passphrase": pass } });
export const verifyAdminPass = async (pass) => {
  const { data } = await api.post("/admin/verify", null, adminHeaders(pass));
  return data;
};
export const fetchAdminStats = async (pass) => {
  const { data } = await api.get("/admin/stats", adminHeaders(pass));
  return data;
};

// ---- Sponsors ----
export const fetchActiveSponsors = async () => {
  const { data } = await api.get("/sponsors/active");
  return data;
};
export const upsertSponsor = async (payload) => {
  const { data } = await api.post("/sponsors/entry", payload);
  return data;
};
export const fetchMySponsor = async (token) => {
  const { data } = await api.get("/sponsors/mine", { params: { token } });
  return data;
};
export const trackSponsorClick = async (sponsorId) => {
  const { data } = await api.get(`/sponsors/click/${sponsorId}`);
  return data;
};

// ---- Books ----
export const fetchBooks = async (category) => {
  const params = category ? { category } : undefined;
  const { data } = await api.get("/books/list", { params });
  return data;
};
export const trackBookClick = async (bookId) => {
  const { data } = await api.get(`/books/click/${bookId}`);
  return data;
};

// ---- Centennial ebook + author submissions ----
export const joinEbookWaitlist = async (email, source = "ebook_page") => {
  const { data } = await api.post("/ebook/waitlist", { email, source });
  return data;
};
export const fetchWaitlistCount = async () => {
  const { data } = await api.get("/ebook/waitlist/count");
  return data;
};
export const submitAuthorBook = async (payload) => {
  const { data } = await api.post("/ebook/submissions", payload);
  return data;
};
export const fetchAdminSubmissions = async (pass, status) => {
  const params = status ? { status } : undefined;
  const { data } = await api.get("/ebook/admin/submissions", {
    ...adminHeaders(pass),
    params,
  });
  return data;
};
export const updateAdminSubmission = async (pass, submissionId, payload) => {
  const { data } = await api.post(
    `/ebook/admin/submissions/${submissionId}`,
    payload,
    adminHeaders(pass),
  );
  return data;
};
export const fetchAdminWaitlist = async (pass) => {
  const { data } = await api.get("/ebook/admin/waitlist", adminHeaders(pass));
  return data;
};

// ---- Medication library ----
export const fetchMedLibrary = async (params = {}) => {
  const { data } = await api.get("/medications/library", { params });
  return data;
};
export const fetchMedLookalikes = async (name) => {
  const { data } = await api.get("/medications/lookalikes", { params: { name } });
  return data;
};
// ---- Med library admin CRUD ----
export const fetchAdminMedLibrary = async (pass) => {
  const { data } = await api.get("/medications/admin/library", adminHeaders(pass));
  return data;
};
export const upsertAdminMedLibrary = async (pass, payload) => {
  const { data } = await api.post("/medications/admin/library", payload, adminHeaders(pass));
  return data;
};
export const deleteAdminMedLibrary = async (pass, slug) => {
  const { data } = await api.delete(`/medications/admin/library/${slug}`, adminHeaders(pass));
  return data;
};
// ---- Med feedback (patients / caregivers) ----
export const submitMedFeedback = async (payload) => {
  const { data } = await api.post("/medications/feedback", payload);
  return data;
};
export const fetchAdminMedFeedback = async (pass, status) => {
  const params = status ? { status } : undefined;
  const { data } = await api.get("/medications/admin/feedback", { ...adminHeaders(pass), params });
  return data;
};
export const updateAdminMedFeedback = async (pass, id, status) => {
  const { data } = await api.post(`/medications/admin/feedback/${id}`, { status }, adminHeaders(pass));
  return data;
};

// ---- Readiness milestone + share ----
export const submitReadinessMilestone = async (payload) => {
  const { data } = await api.post("/readiness/milestone", payload);
  return data;
};
export const createReadinessShare = async (workbook) => {
  const { data } = await api.post("/readiness/share", { workbook });
  return data;
};
export const fetchReadinessShare = async (shareId) => {
  const { data } = await api.get(`/readiness/share/${shareId}`);
  return data;
};
export const fetchAdminMilestones = async (pass) => {
  const { data } = await api.get("/readiness/admin/milestones", adminHeaders(pass));
  return data;
};
export const adminResendMilestone = async (pass, email, milestone) => {
  const { data } = await api.post("/readiness/admin/milestones/resend", { email, milestone }, adminHeaders(pass));
  return data;
};

// ────────── Social manager (admin-only) ──────────
export const fetchSocialSources = async (pass) => {
  const { data } = await api.get("/social/sources", adminHeaders(pass));
  return data;
};
export const generateSocialVariants = async (pass, payload) => {
  const { data } = await api.post("/social/generate", payload, {
    ...adminHeaders(pass),
    timeout: 90000,
  });
  return data;
};
export const fetchSocialChannels = async (pass) => {
  const { data } = await api.get("/social/channels", adminHeaders(pass));
  return data;
};
export const publishSocialPost = async (pass, payload) => {
  const { data } = await api.post("/social/publish", payload, adminHeaders(pass));
  return data;
};
export const fetchSocialHistory = async (pass) => {
  const { data } = await api.get("/social/history", adminHeaders(pass));
  return data;
};
export const buildSocialDigest = async (pass, payload) => {
  const { data } = await api.post("/social/digest", payload, {
    ...adminHeaders(pass),
    timeout: 180000,
  });
  return data;
};
export const fetchAutopilot = async (pass) => {
  const { data } = await api.get("/social/autopilot", adminHeaders(pass));
  return data;
};
export const saveAutopilot = async (pass, payload) => {
  const { data } = await api.post("/social/autopilot", payload, adminHeaders(pass));
  return data;
};
export const runAutopilotNow = async (pass) => {
  const { data } = await api.post("/social/autopilot/run_now", null, {
    ...adminHeaders(pass),
    timeout: 300000,
  });
  return data;
};
export const fetchNewsletterStatus = async (pass) => {
  const { data } = await api.get("/newsletter/status", adminHeaders(pass));
  return data;
};
export const sendNewsletterNow = async (pass) => {
  const { data } = await api.post("/newsletter/send_now", null, {
    ...adminHeaders(pass),
    timeout: 300000,
  });
  return data;
};

export const upsertMedShareCard = async (payload) => {
  const { data } = await api.post("/med-cards", payload);
  return data;
};

export const fetchMedShareCard = async (cardId) => {
  const { data } = await api.get(`/med-cards/${cardId}`);
  return data;
};

export const deleteMedShareCard = async (cardId, editKey) => {
  const { data } = await api.delete(`/med-cards/${cardId}`, {
    params: { edit_key: editKey },
  });
  return data;
};
